/**
 * Draft filter state hook for filter surfaces with commit semantics
 *
 * Provides a local copy of filter values that does not affect the
 * committed FilterState until explicitly committed via commit().
 * Initializes from the current committed state on mount; discarded
 * automatically when the panel unmounts (Cancel path). For mounted
 * surfaces (e.g. FilterSheet drawer), call reinitialize() when the
 * surface opens to re-snapshot the current committed state.
 * @module features/scouting/hooks/useDraftFilterState
 */

import { useCallback, useState } from "react";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";

import type { FilterSnapshot, FilterState } from "@/hooks/useFilterState";
import { DEFAULT_FILTER_SNAPSHOT, takeSnapshot } from "@/hooks/useFilterState";

/**
 * Counts truthy predicates — used for draft filter count badges
 * @param predicates - Boolean conditions to count
 * @returns Number of truthy values
 */
const countActive = (...predicates: readonly boolean[]): number =>
  predicates.filter(Boolean).length;

/**
 * Counts the number of active custom stat range filters,
 * considering only metrics that currently exist for the org.
 * @param customStatRanges - Custom stat ranges keyed by stat name
 * @param availableMetricIds - IDs of currently available custom metrics
 * @returns Number of active custom stat filters
 */
const countActiveCustomStats = (
  customStatRanges: Readonly<Record<string, readonly [number, number]>>,
  availableMetricIds: ReadonlySet<string>
): number =>
  Object.entries(customStatRanges).filter(
    ([key, range]) =>
      availableMetricIds.has(key) &&
      isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
  ).length;

/** Return type for the useDraftFilterState hook */
export interface DraftFilterState {
  /** Current draft filter values */
  readonly draft: FilterSnapshot;
  /** Applies a partial update to draft values */
  readonly setDraftField: (update: Partial<FilterSnapshot>) => void;
  /**
   * Applies a functional update to draft values — use when the new
   * value depends on the previous value (e.g., toggling an ID)
   */
  readonly updateDraft: (
    updater: (prev: FilterSnapshot) => Partial<FilterSnapshot>
  ) => void;
  /** Number of active filters in the draft (not committed) */
  readonly activeFilterCount: number;
  /** Resets all draft filters to defaults */
  readonly clearDraft: () => void;
  /** Resets sporting filters while preserving hidden financial ranges */
  readonly clearSportingDraft: () => void;
  /** Pushes draft values into the committed FilterState */
  readonly commit: () => void;
  /**
   * Re-snapshots the current committed state into the draft —
   * call this when a mounted surface re-opens (e.g. drawer open
   * transition) to pick up any committed changes since last close
   */
  readonly reinitialize: () => void;
}

/**
 * Manages a local draft copy of filter state for filter surfaces.
 * On mount, snapshots the current committed state. Changes stay local
 * until commit() is called. When the panel unmounts without committing,
 * the draft is discarded. For mounted surfaces, call reinitialize()
 * on the open transition to re-snapshot.
 * @param filterState - The committed FilterState to snapshot and commit to
 * @param availableMetricIds - IDs of currently available org-level custom metrics;
 *   only ranges whose key exists in this set are counted as active, preventing
 *   orphaned ranges (from deleted/renamed metrics) from inflating the badge count
 * @returns Draft state, updaters, active count, clear, commit, and reinitialize
 */
const useDraftFilterState = (
  filterState: FilterState,
  availableMetricIds: ReadonlySet<string> = new Set()
): DraftFilterState => {
  const [draft, setDraft] = useState<FilterSnapshot>(() =>
    takeSnapshot(filterState)
  );

  const setDraftField = useCallback((update: Partial<FilterSnapshot>) => {
    setDraft(prev => ({ ...prev, ...update }));
  }, []);

  const updateDraft = useCallback(
    (updater: (prev: FilterSnapshot) => Partial<FilterSnapshot>) => {
      setDraft(prev => ({ ...prev, ...updater(prev) }));
    },
    []
  );

  const clearDraft = useCallback(() => {
    setDraft(DEFAULT_FILTER_SNAPSHOT);
  }, []);

  const clearSportingDraft = useCallback(() => {
    setDraft(current => ({
      ...DEFAULT_FILTER_SNAPSHOT,
      contractMonthsRange: current.contractMonthsRange,
      valuationRange: current.valuationRange,
    }));
  }, []);

  const commit = useCallback(() => {
    filterState.restoreSnapshot(draft);
  }, [draft, filterState]);

  const reinitialize = useCallback(() => {
    setDraft(takeSnapshot(filterState));
  }, [filterState]);

  const rangeFilterCount = countActive(
    isRangeActive(draft.ageRange, DEFAULT_AGE_RANGE),
    isRangeActive(draft.contractMonthsRange, DEFAULT_CONTRACT_MONTHS_RANGE),
    isRangeActive(draft.valuationRange, DEFAULT_VALUATION_RANGE),
    isRangeActive(draft.gprRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.fitScoreRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.teamStyleFitRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.carryingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.dribblingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.passingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.shootingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.gkPassingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.gkShotStoppingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(draft.gkPositioningRange, DEFAULT_SCORE_RANGE)
  );

  const customStatActiveCount = countActiveCustomStats(
    draft.customStatRanges,
    availableMetricIds
  );

  const activeFilterCount =
    draft.selectedPositionIds.length +
    draft.selectedClubIds.length +
    draft.selectedScoutIds.length +
    draft.selectedNationalityIds.length +
    draft.selectedLeagueIds.length +
    draft.selectedCompetitionIds.length +
    draft.selectedRoleArchetypes.length +
    rangeFilterCount +
    customStatActiveCount;

  return {
    draft,
    setDraftField,
    updateDraft,
    activeFilterCount,
    clearDraft,
    clearSportingDraft,
    commit,
    reinitialize,
  };
};

export default useDraftFilterState;
